import React, {
  memo, useState, useEffect, useRef,
} from 'react';
import {
  Grid, makeStyles,
  Divider, List, CircularProgress, RadioGroup, FormControlLabel, Radio,
  Button, TextareaAutosize, TextField,
} from '@material-ui/core';
import { FormLayout, FormQuestion, FormHeader } from '../Common';
import { UI_CONFIG, QUESTION_TYPE } from '../../constants/Constants';
import StarRating from '../Common/StarRating/index';
import { userConstants } from '../../locale/userTexts';
import activeBackIcon from '../../assets/activeBack.svg';
import inactiveBackIcon from '../../assets/inativeBack.svg';
import './index.scss';

// WO-11861: selecting an option saves and advances on its own, so the delay has to be long
// enough to register the selected state without feeling like a wait. Ticket allows 200-500ms.
// WO-12648: 300ms felt too fast for users to register their selection — raised to 500ms.
const AUTO_ADVANCE_DELAY_MS = 500;

// Only option-pick types auto-advance — open-ended text and comment fields must not, since
// the user is still typing when the response first becomes non-empty.
const AUTO_ADVANCE_TYPES = [
  QUESTION_TYPE.SINGLE_SELECT,
  QUESTION_TYPE.SCENARIO_CHOICE,
  QUESTION_TYPE.FORCED_CHOICE,
  QUESTION_TYPE.IMAGE_CHOICE,
  QUESTION_TYPE.FUN_METER,
  QUESTION_TYPE.ILLUSTRATED_SCENARIO,
];

// A question with a free-text follow-up ("Other", or a comment box) can't auto-advance: the
// user is mid-thought when the response first reads as complete. Those keep the Next CTA.
const needsManualSubmit = (d) => Boolean(d?.commentType);

// Shared by the Next-CTA validity effect and by auto-advance, so tapping to advance applies
// exactly the same completeness rules the Next button enforced.
const isQuestionAnswered = (d) => {
  if (d.isRequired && (d.response <= -1 || d.response === null || d.response === '')) return false;
  if (d.isRequired && d?.commentType === 'ANSWER' && d?.response === d?.options.length - 1
    && (!d?.answer || d?.answer.trim().length === 0)) return false;
  if (d.isRequired && d?.commentType === 'COMMENT' && d?.comment?.isRequired
    && (!d?.commentText || d?.commentText?.trim().length === 0)) return false;
  return true;
};

const useStyles = makeStyles(() => ({
  root: {
    paddingLeft: '0',
    paddingTop: '0px',
  },
  formlabel: {
    '& .MuiFormControlLabel-label': {
      wordBreak: 'break-word',
      fontFamily: 'Frank Ruhl Libre',
    },
  },
  radioRoot: {
    height: '24px',
    width: '24px',
    padding: '4px',
    color: '#999', // default border color
    '&.Mui-checked': {
      color: '#4caf50', // checked border color
    },
  },
}));

function CardImage({ mediaUrls, alt, genderType }) {
  // mediaUrls can be flat { fileUrl, lowResolutionUrl } or gender-keyed { male: {...}, female: {...} }
  const urls = (genderType && mediaUrls?.[genderType]) ? mediaUrls[genderType] : mediaUrls;
  const [src, setSrc] = useState(urls?.lowResolutionUrl);

  useEffect(() => {
    setSrc(urls?.lowResolutionUrl);
  }, [genderType, urls?.lowResolutionUrl]);

  return (
    <img
      src={src}
      alt={alt}
      className="card-img"
      onLoad={() => {
        if (src === urls?.lowResolutionUrl && urls?.fileUrl) {
          setSrc(urls.fileUrl);
        }
      }}
      onError={() => {
        // lowResolutionUrl missing — fall back to fileUrl directly
        if (src !== urls?.fileUrl && urls?.fileUrl) {
          setSrc(urls.fileUrl);
        }
      }}
    />
  );
}

function RenderQuestionType(props) {
  const {
    type, options, onChange, response, language, commentType, commentTypeData, onOtherChange,
    handleRadioClick, onCardSelect, isV2, genderType, isMobile, mediaUrls,
  } = props;
  let ratingConfig;

  const classes = useStyles();
  const renderCount = (Limit) => {
    const fields = [];
    for (let index = 1; index <= Limit; index += 1) {
      fields.push(<div className="star-rating">{index}</div>);
    }
    return fields;
  };
  switch (type) {
    case QUESTION_TYPE.SINGLE_SELECT:
      return (
        <List className={classes.root}>
          <RadioGroup aria-label="quiz" name="quiz" value={response} className="form_response_radio">
            {Array.isArray(options) && options.map((option, index) => {
              const { option: optionText } = option;
              return (
                <>
                  <FormControlLabel
                    className={classes.formlabel}
                    key={`option-${index}-${optionText}`}
                    value={index}
                    control={<Radio className="question_radio_button" onClick={handleRadioClick} />}
                    label={optionText}
                  />
                  {(commentType === 'ANSWER' && option?.isOther && response === options?.length - 1)
                    && (
                      <TextField
                        style={{
                          width: '100%',
                          paddingBottom: '8px',
                        }}
                        id="outlined-textarea"
                        label={option?.placeholderText}
                        placeholder={option?.option}
                        multiline
                        variant="outlined"
                        name="answer"
                        onChange={(e) => onOtherChange(e)}
                      />
                    )}
                </>
              );
            })}
          </RadioGroup>
          {commentType === 'COMMENT' && (
            <>
              <TextField
                style={{
                  width: '100%',
                  paddingBottom: '8px',
                  marginTop: '8px',
                }}
                id="outlined-textarea"
                placeholder={commentTypeData?.placeholderText}
                multiline
                variant="outlined"
                label={commentTypeData?.label}
                name="commentText"
                required={commentTypeData?.isRequired}
                onChange={(e) => onOtherChange(e)}
              />
            </>
          )}
        </List>
      );
    case QUESTION_TYPE.OPEN_ENDED:
      return (
        <TextareaAutosize
          className="text-area-style"
          rowsMin={4}
          placeholder={userConstants(language).textAreaPlaceholder}
          value={response}
          onChange={onChange}
        />
      );
    case QUESTION_TYPE.RATING:
      ratingConfig = {
        size: isMobile ? 'small' : 'medium',
        classNames: 'stars',
        edit: true,
        count: options?.length,
        onChange,
      };
      return (
        <div className="ratingStars">
          <StarRating ratingConfig={ratingConfig} />
          <div className="star-count-container">
            {renderCount(options?.length)}
          </div>
        </div>
      );
    case QUESTION_TYPE.SCENARIO_CHOICE:
      return (
        <div className={`scenario-choice-container${isV2 && isMobile ? ' mobile' : ''}`}>
          {Array.isArray(options) && options.map((option, idx) => (
            <div
              key={idx}
              role="button"
              tabIndex={0}
              className={`scenario-card${response === idx ? ' selected' : ''}`}
              onClick={() => onCardSelect(idx)}
              onKeyDown={(e) => e.key === 'Enter' && onCardSelect(idx)}
            >
              {option.mediaUrls && (
                <CardImage mediaUrls={option.mediaUrls} alt={option.option} genderType={genderType} />
              )}
              <p className="card-label">{option.option}</p>
            </div>
          ))}
        </div>
      );
    case QUESTION_TYPE.FORCED_CHOICE:
      return (
        <div className={`forced-choice-container${isV2 ? ' v2' : ''}`}>
          {Array.isArray(options) && options.map((option, idx) => (
            <div
              key={idx}
              role="button"
              tabIndex={0}
              className={`forced-card${response === idx ? ' selected' : ''}`}
              onClick={() => onCardSelect(idx)}
              onKeyDown={(e) => e.key === 'Enter' && onCardSelect(idx)}
            >
              {option.mediaUrls && (
                <CardImage mediaUrls={option.mediaUrls} alt={option.option} genderType={genderType} />
              )}
              <p className="card-label">{option.option}</p>
            </div>
          ))}
        </div>
      );
    case QUESTION_TYPE.IMAGE_CHOICE:
      return (
        <div className={`image-choice-container${isV2 && isMobile ? ' mobile' : ''}`}>
          {Array.isArray(options) && options.map((option, idx) => (
            <div
              key={idx}
              role="button"
              tabIndex={0}
              className={`scenario-card${response === idx ? ' selected' : ''}`}
              onClick={() => onCardSelect(idx)}
              onKeyDown={(e) => e.key === 'Enter' && onCardSelect(idx)}
            >
              {option.mediaUrls && (
                <CardImage mediaUrls={option.mediaUrls} alt={option.option} genderType={genderType} />
              )}
              <p className="card-label">{option.option}</p>
            </div>
          ))}
        </div>
      );
    case QUESTION_TYPE.FUN_METER: {
      const totalOpts = Array.isArray(options) ? options.length : 4;
      const thumbPct = response >= 0 ? (response / (totalOpts - 1)) * 100 : null;

      if (isMobile) {
        // ── Mobile: continuous vertical track on the left, stacked cards on the right ──
        const cardH = 70;
        const cardGap = 25;
        const step = cardH + cardGap;
        const n = Array.isArray(options) ? options.length : 0;
        // 24px top offset so first card center aligns to first dot center (matches Figma pt-24px)
        const topOffset = 24;
        const trackH = n > 0 ? topOffset + n * cardH + (n - 1) * cardGap : 0;
        // Center of card i within the track column
        const cardCenter = (i) => topOffset + i * step + cardH / 2;
        // Gray bar: starts at track top (above first card), ends at last card center
        const barTop = 0;
        const barH = n > 0 ? cardCenter(n - 1) : 0;
        // Purple fill: from track top to selected card center
        const fillH = response >= 0 ? cardCenter(response) : 0;
        // Dot top: card center minus half dot (4px)
        const dotTopPx = (i) => cardCenter(i) - 4;
        // Thumb top: card center minus half thumb (12px)
        const thumbTopPx = response >= 0 ? cardCenter(response) - 12 : null;

        return (
          <div className="fun-meter-v-wrap">
            {/* Left: continuous track with gray bar, purple fill, dots, thumb */}
            <div className="fun-meter-v-track" style={{ height: `${trackH}px` }}>
              <div className="fun-meter-v-bar" style={{ top: `${barTop}px`, height: `${barH}px` }} />
              <div className="fun-meter-v-fill" style={{ top: `${barTop}px`, height: `${fillH}px` }} />
              {Array.isArray(options) && options.map((_, idx) => (
                <div key={idx} className="fun-meter-v-dot" style={{ top: `${dotTopPx(idx)}px` }} />
              ))}
              {thumbTopPx !== null && (
                <div className="fun-meter-v-thumb" style={{ top: `${thumbTopPx}px` }} />
              )}
            </div>
            {/* Right: stacked cards */}
            <div className="fun-meter-v-cards">
              {Array.isArray(options) && options.map((option, idx) => (
                <div
                  key={idx}
                  role="button"
                  tabIndex={0}
                  className={`fun-meter-card-v${response === idx ? ' selected' : ''}`}
                  onClick={() => onCardSelect(idx)}
                  onKeyDown={(e) => e.key === 'Enter' && onCardSelect(idx)}
                >
                  {option.mediaUrls && (
                    <CardImage mediaUrls={option.mediaUrls} alt={option.option} genderType={genderType} />
                  )}
                  <span className="card-label">{option.option}</span>
                </div>
              ))}
            </div>
          </div>
        );
      }

      // ── Desktop: horizontal cards + slider track below ──
      //
      // Figma: cards are fixed 160px wide, justify-center in the container.
      //   Left margin before first card = (W - (176n - 16)) / 2 = W/2 - 88(n-1) - 8
      //   First card center from bar left = W/2 - 88(n-1)
      //   Last  card center from bar left = W/2 + 88(n-1)
      //
      // Bar:  left=0 (extends 76px before first card) → right = 50% - 88(n-1)px
      // Fill: left=0, width = 50% - 88(n-1)px + k×176px  (reaches card[k] center)
      const n = totalOpts;
      const offset = 88 * (n - 1); // px from slider center to first/last card center
      // Bar's right inset = first card's center distance from the container's right
      const barRight = `calc(50% - ${offset}px)`;
      // Fill width reaches card[response]'s center from bar-left (0)
      const fillWidth = response >= 0
        ? `calc(50% + ${176 * response - offset}px)`
        : '0%';

      return (
        <div className="fun-meter-desktop">
          <div className="fun-meter-options-h">
            {Array.isArray(options) && options.map((option, idx) => (
              <div
                key={idx}
                role="button"
                tabIndex={0}
                className={`fun-meter-card-h${response === idx ? ' selected' : ''}`}
                onClick={() => onCardSelect(idx)}
                onKeyDown={(e) => e.key === 'Enter' && onCardSelect(idx)}
              >
                {option.mediaUrls && (
                  <CardImage mediaUrls={option.mediaUrls} alt={option.option} genderType={genderType} />
                )}
                <p className="card-label">{option.option}</p>
              </div>
            ))}
          </div>
          <div className="fun-meter-slider-h">
            {/* Gray bar: 0 → last card center; extends before first card since cards are centered */}
            <div className="fun-meter-track-bg" style={{ left: 0, right: barRight }} />
            {/* Purple fill: sibling so 100% = slider width; reaches card[response] center */}
            <div className="fun-meter-track-fill" style={{ width: fillWidth }} />
            <div className="fun-meter-dots-row">
              {Array.isArray(options) && options.map((_, idx) => (
                <div key={idx} className="fun-meter-dot-cell">
                  {response === idx
                    ? <div className="fun-meter-thumb" />
                    : idx > response
                      ? <div className="fun-meter-dot-h" />
                      : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }
    case QUESTION_TYPE.ILLUSTRATED_SCENARIO: {
      const { mediaUrls: questionMediaUrls } = props;
      return (
        <div className={`illustrated-scenario-container${isMobile ? ' mobile' : ''}`}>
          <div className="illustrated-scenario-options">
            {Array.isArray(options) && options.map((option, idx) => (
              <div
                key={idx}
                role="button"
                tabIndex={0}
                className={`illustrated-scenario-card${response === idx ? ' selected' : ''}`}
                onClick={() => onCardSelect(idx)}
                onKeyDown={(e) => e.key === 'Enter' && onCardSelect(idx)}
              >
                <Radio
                  className="question_radio_button"
                  checked={response === idx}
                  onChange={() => onCardSelect(idx)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ padding: 0, color: response === idx ? '#F68721' : '#999' }}
                />
                <span className="illustrated-scenario-label">{option.option}</span>
              </div>
            ))}
          </div>
          {questionMediaUrls && (
            <div className="illustrated-scenario-image-wrap">
              <CardImage mediaUrls={questionMediaUrls} alt="scenario" genderType={genderType} className="illustrated-scenario-img" />
            </div>
          )}
        </div>
      );
    }
    default:
      return null;
  }
}

const QuestionSet = (props) => {
  const {
    questionObj, index, onChange, language, totalCount, isV2, genderType, displayIndex, isMobile,
    tapToAdvance,
  } = props;
  const {
    questionText, options, response, description, type, imageUrl, commentType, comment, isRequired, examples,
    simplifiedQuestion, mediaUrls,
  } = questionObj;

  const onChangeOption = (event) => {
    const { value } = event.target;
    const newQuestionObj = {
      ...questionObj,
      response: value,
    };
    onChange(index, { ...newQuestionObj });
  };

  const onOtherChange = (event) => {
    const { value, name } = event.target;
    const newQuestionObj = {
      ...questionObj,
      [name]: value,
    };
    questionObj.response = { ...questionObj.response, responseText: value };
    onChange(index, { ...newQuestionObj });
  };

  // Re-tapping the selected option normally clears it. Under tap-to-advance that would let a
  // double-tap land on an empty response mid-delay, so selection is one-way there.
  function handleClick(event) {
    const { value } = event.target;
    const isDeselect = Number(value) === response && !tapToAdvance;
    const newQuestionObj = {
      ...questionObj,
      response: isDeselect ? Number(-1) : Number(value),
    };
    onChange(index, { ...newQuestionObj }, { fromOptionPick: !isDeselect });
  }

  function handleCardSelect(optionIndex) {
    const isDeselect = optionIndex === response && !tapToAdvance;
    const newQuestionObj = {
      ...questionObj,
      response: isDeselect ? -1 : optionIndex,
    };
    onChange(index, { ...newQuestionObj }, { fromOptionPick: !isDeselect });
  }

  return (
    <>
      <Grid container spacing={2} className="question-number-continer">
        <FormQuestion
          index={index}
          displayIndex={displayIndex}
          questionText={simplifiedQuestion}
          imageUrl={imageUrl}
          isRequired={isRequired}
          examples={examples}
          totalCount={totalCount}
          language={language}
          hideProgress={tapToAdvance}
        />
        <Grid item xs={12} md={12} className="question-type-container">
          <RenderQuestionType
            type={type}
            commentType={commentType}
            options={options}
            response={response}
            onChange={onChangeOption}
            language={language}
            commentTypeData={comment}
            onOtherChange={onOtherChange}
            handleRadioClick={handleClick}
            onCardSelect={handleCardSelect}
            isV2={isV2}
            genderType={genderType}
            isMobile={isMobile}
            mediaUrls={mediaUrls}
          />
          {(examples?.urban || examples?.rural) && (
            <ul className="list_of_exmpl_wrapper">
              <li className="exmpl_item">{examples?.urban}</li>
              <li className="exmpl_item">{examples?.rural}</li>
            </ul>
          )}
        </Grid>
      </Grid>
      {!isV2 && (index + 1 < totalCount) && (
        <Divider
          variant="middle"
          style={{
            margin: '0.5rem 0 1.2rem 0',
            backgroundColor: '#FCE1D4',
          }}
        />
      )}
    </>
  );
};

const RespondentForm = (props) => {
  const {
    questionnaire, questions,
    onSubmit, isLoading, showHeader, language, response, questionIdMap,
    version, enableBack, isLast, genderType, questionNumber, overallTotalCount, isMobile,
    tapToAdvance, enableForward,
  } = props;
  const isV2 = version === '2';
  // WO-11861 applies to v2 only. Gating here means every downstream consumer — auto-advance,
  // the one-way select, the hidden in-card counter — is v2-only by construction.
  const tapMode = isV2 && tapToAdvance;
  const advanceTimerRef = useRef(null);
  // Guards the window between the timer firing and the next question replacing this one.
  const hasAdvancedRef = useRef(false);
  const [questionnaireData, setQuestionnaire] = useState({
    title: '',
    descriptions: '',
  });
  const [questionsData, setAllQuestions] = useState(null);
  const [isValidResponse, setIsValidResponse] = useState(true);
  const [isAdvancing, setIsAdvancing] = useState(false);

  // Tap-to-advance only makes sense for a single option-pick question on the page. A page with
  // several questions, or one with a free-text follow-up, keeps the Next CTA so the user is
  // never left with no way to continue.
  const canAutoAdvance = tapMode
    && Array.isArray(questionsData)
    && questionsData.length === 1
    && !questionsData.some(needsManualSubmit);

  useEffect(() => {
    if (questionsData) {
      setIsValidResponse(questionsData.every(isQuestionAnswered));
    }
  }, [setIsValidResponse, questionsData]);

  useEffect(() => {
    const INDEXED_TYPES = [
      QUESTION_TYPE.SINGLE_SELECT,
      QUESTION_TYPE.SCENARIO_CHOICE,
      QUESTION_TYPE.FORCED_CHOICE,
      QUESTION_TYPE.IMAGE_CHOICE,
      QUESTION_TYPE.FUN_METER,
      QUESTION_TYPE.ILLUSTRATED_SCENARIO,
    ];
    const getResponse = (question) => {
      const responseData = response?.responses?.find((item) => item?.questionId === question?.id);
      const stored = responseData?.response?.[question.type];
      if (!stored) return -1;
      // Fall back to weightage when the option text doesn't match — the questionnaire language was switched
      let findIndex = question?.options?.findIndex((item) => item?.option === stored?.option);
      if (findIndex < 0) {
        findIndex = question?.options?.findIndex((item) => item?.weightage === stored?.weightage);
      }
      return findIndex >= 0 ? findIndex : -1;
    };
    if (questionnaire) {
      setQuestionnaire({ ...questionnaire });
    }
    if (questions && Array.isArray(questions)) {
      setAllQuestions([...questions.map((d) => ({
        ...d, response: INDEXED_TYPES.includes(d.type) ? getResponse(d) : null,
      }))]);
      // A new question arrived — re-arm auto-advance for it.
      hasAdvancedRef.current = false;
      setIsAdvancing(false);
    }
  }, [questionnaire, questions, response]);

  useEffect(() => () => {
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
  }, []);

  // Strip media fields from option before storing in response — media is always
  // read from the question at display time, so storing URLs in responses causes
  // broken references when storage buckets are migrated.
  const stripMedia = (option) => {
    if (!option) return option;
    const { mediaUrls, media, ...rest } = option;
    return rest;
  };

  const getValue = (d) => {
    let value;
    switch (d.type) {
      case QUESTION_TYPE.SINGLE_SELECT:
      case QUESTION_TYPE.SCENARIO_CHOICE:
      case QUESTION_TYPE.FORCED_CHOICE:
      case QUESTION_TYPE.IMAGE_CHOICE:
      case QUESTION_TYPE.FUN_METER:
      case QUESTION_TYPE.ILLUSTRATED_SCENARIO:
        value = stripMedia(d.options[d.response]);
        break;
      case QUESTION_TYPE.OPEN_ENDED:
        value = d.response;
        break;
      case QUESTION_TYPE.RATING:
        value = stripMedia(d.options[d.response - 1]);
        break;
      default:
        break;
    }
    return value;
  };

  // True only when the picked value matches the answer already saved for this question — used to
  // tell the parent a review re-confirmation is not a fresh answer (skips the "answered" event).
  const isUnchangedFromSaved = (d) => {
    const savedResponses = response?.responses;
    if (!Array.isArray(savedResponses) || savedResponses.length === 0) return false;
    const saved = savedResponses.find((item) => item?.questionId === d?.id)?.response?.[d.type];
    if (!saved) return false;
    const current = getValue(d);
    if (!current) return false;
    if (current.option != null && saved.option != null) return current.option === saved.option;
    if (current.weightage != null && saved.weightage != null) return current.weightage === saved.weightage;
    return false;
  };

  // overrideQuestions lets auto-advance submit the answer it just recorded, without waiting a
  // render for questionsData to catch up. Guarded with isArray because the Next CTA passes a
  // click event as the first argument.
  const handleSubmit = async (overrideQuestions) => {
    const source = Array.isArray(overrideQuestions) ? overrideQuestions : questionsData;
    const responses = [];
    source.forEach((d) => {
      if (d.response !== -1) {
        if (d?.commentType === 'ANSWER' && d?.answer?.length > 0) {
          responses.push({
            questionId: d.id,
            response: {
              [d.type]: {
                ...getValue(d),
                isOther: true,
                responseText: d?.answer,
              },
            },
            tags: d.tags,
          });
        } else if (d?.commentType === 'COMMENT' && d?.commentText?.length > 0) {
          responses.push({
            questionId: d.id,
            response: {
              [d.type]: getValue(d),
              comment: {
                responseText: d?.commentText,
              },
            },
            tags: d.tags,
          });
        } else {
          responses.push({
            questionId: d.id,
            response: {
              [d.type]: getValue(d),
            },
            tags: d.tags,
          });
        }
      }
    });
    // changed=false when every question on the page still holds its saved answer.
    const changed = !source.every(isUnchangedFromSaved);
    await onSubmit(responses, changed);
  };

  const onChange = (index, updatedData, meta) => {
    // Once the answer is on its way to the server, further taps must not silently move the
    // selected state to an option that was never submitted.
    if (canAutoAdvance && hasAdvancedRef.current) return;

    const newQuestions = [...questionsData];
    newQuestions[index] = updatedData;
    setAllQuestions(newQuestions);

    if (!canAutoAdvance) return;
    // Only a deliberate option tap advances — typing in an "Other"/comment field also lands
    // here, and pausing to think would otherwise submit half-typed text.
    if (!meta?.fromOptionPick) return;
    if (!AUTO_ADVANCE_TYPES.includes(updatedData.type)) return;
    if (!newQuestions.every(isQuestionAnswered)) return;
    // Changing the pick before the delay elapses re-arms rather than queuing a second submit.
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    setIsAdvancing(true);
    advanceTimerRef.current = setTimeout(() => {
      advanceTimerRef.current = null;
      hasAdvancedRef.current = true;
      handleSubmit(newQuestions);
    }, AUTO_ADVANCE_DELAY_MS);
  };

  // Back/Forward cancel a pending advance, otherwise both the navigation and the auto-submit
  // move the page and the user lands two questions away.
  const cancelPendingAdvance = () => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
    setIsAdvancing(false);
  };

  const handleBack = () => {
    cancelPendingAdvance();
    window.parent.postMessage({ type: 'NAVIGATE_BACK' }, '*');
  };

  const handleForward = () => {
    cancelPendingAdvance();
    window.parent.postMessage({ type: 'NAVIGATE_FORWARD' }, '*');
  };

  return (
    isLoading ? (
      <div className="loaderCont">
        <CircularProgress size={UI_CONFIG.loaderSize} />
      </div>
    ) : (
      <div className="newQuestCont">
        {showHeader && (
          <div className="formSect">
            <FormHeader data={questionnaireData} />
          </div>
        )}
        <div className="formSect">
          <FormLayout>
            <form>
              {
                Array.isArray(questionsData) && questionsData.map((d, index) => (
                  <QuestionSet
                    selected
                    key={`question-set-${index}`}
                    questionObj={d}
                    index={index}
                    onChange={onChange}
                    language={language}
                    totalCount={overallTotalCount || questionsData?.length}
                    displayIndex={overallTotalCount && questionNumber ? questionNumber : undefined}
                    isV2={isV2}
                    genderType={genderType}
                    isMobile={isMobile}
                    tapToAdvance={tapMode}
                  />
                ))
              }
            </form>
            {isV2 && (
              <>
                <div className={`v2-actions${isMobile ? ' mobile' : ''}${canAutoAdvance ? ' tap-mode' : ''}`}>
                  {/* Question 1 has nowhere to go back to, so it shows no Back control at all
                      rather than a greyed-out one. */}
                  {canAutoAdvance && !enableBack ? (
                    <span className="v2-nav-spacer" />
                  ) : (
                    <Button
                      type="button"
                      className="v2-back-btn"
                      disabled={!enableBack || isAdvancing}
                      onClick={handleBack}
                    >
                      <img src={enableBack ? activeBackIcon : inactiveBackIcon} alt="back" />
                    </Button>
                  )}
                  {/* Under tap-to-advance the answer itself submits, so the only forward
                      affordance is for re-walking questions already answered. */}
                  {canAutoAdvance ? (
                    enableForward && (
                      <Button
                        type="button"
                        className="v2-forward-btn"
                        disabled={isAdvancing}
                        onClick={handleForward}
                      >
                        <img src={activeBackIcon} alt="forward" />
                      </Button>
                    )
                  ) : (
                    <Button
                      type="button"
                      className="themeBtn v2-next-btn"
                      disabled={!isValidResponse}
                      onClick={handleSubmit}
                    >
                      {isLast ? userConstants(language).submitButtonInLowerCase : userConstants(language).nextButton}
                    </Button>
                  )}
                </div>
                {canAutoAdvance && (
                  <p className="v2-helper-text">{userConstants(language).tapHelperText}</p>
                )}
              </>
            )}
          </FormLayout>
          {!isV2 && (
            <Button
              type="button"
              variant="outlined"
              className="themeBtn"
              disabled={!isValidResponse}
              onClick={handleSubmit}
            >
              {userConstants(language).submitButtonInLowerCase}
            </Button>
          )}
        </div>
      </div>
    )
  );
};

export default memo(RespondentForm);
